// ─── Scheda del socio → 🤖 Bot dei soci ──────────────────────────────────────
//
// 🗣️ 24/09/2026, dal mockup approvato (`mockup/invito-bot-scheda-socio-mockup.html`):
// la segreteria manda al socio il link del bot su WhatsApp.
//
// ⭐ **PERCHÉ ESISTE.** Il bot dei soci si apriva solo dal link di un socio già
// dentro, e il primo non aveva da chi riceverlo. Il bot, a chi non conosce,
// dice «chiama la segreteria»: questo è quello che la segreteria fa.
//
// 🔄 **Dal 25/09/2026 (voce 28, mockup `invito-bot-link-unico-mockup.html`) il
// link è UNO SOLO, uguale per tutti**: non si crea, non scade e non si ritira.
// 🔒 Il bot riconosce il socio dal numero condiviso con REGISTRATI, che è quello
// dell'account Telegram: deve essere il telefono di questa scheda.

import { useCallback, useEffect, useState } from 'react';
import type { InvitoAlBot } from '@pmo/shared';
import { usaInvitoBot } from '../../features/soci/dati.js';
import { Attesa, Guasto } from '../../components/pezzi.js';

const FUSO = 'Europe/Rome';

const quando = (iso: string) => {
  const d = new Date(iso);
  const giorno = d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', timeZone: FUSO });
  const ora = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: FUSO });
  return `il ${giorno} alle ${ora}`;
};
const soloOra = (d: Date) =>
  d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: FUSO });

/**
 * 🗣️ Voce 24 (25/09/2026, dal mockup approvato): mandato il messaggio, il
 * riquadro torna corto da solo e dice cosa si è fatto e a che ora. Resta solo
 * finché la scheda è aperta: il gestionale non lo salva, perché aprire WhatsApp
 * non vuol dire che il socio l'abbia ricevuto.
 */
type Mandato = { come: 'whatsapp' | 'copiato'; alle: Date };

/**
 * Il messaggio che arriva al socio su WhatsApp.
 *
 * 🗣️ Testo approvato il 24/09/2026. Tre scelte sue, e valgono per tutto il bot:
 * ① «assistente virtuale», non «bot» né «chatbot»: lo conoscono tutti, e dice
 *    onestamente che non è una persona;
 * ② **dedicato esclusivamente a te**: l'assistente parla a una persona sola, e
 *    vede solo i dati di quella persona;
 * ③ si comincia da cosa ci si fa, non dal nome dello strumento.
 * ✍️ «Padel Village ASD» senza articolo, e la firma «La Segreteria di Padel Village
 *    ASD»: ritocchi suoi sull'anteprima (il suffisso «ASD» dal 25/09/2026, come nel
 *    primo messaggio del bot e nel benvenuto). In grassetto *assistente virtuale* e, anche in
 *    maiuscolo, *DEDICATO ESCLUSIVAMENTE A TE*, perché è la novità. Gli asterischi
 *    sono il grassetto di WhatsApp. «Gratuitamente»: che non costa niente va detto.
 * ⭐ I passi dopo il link servono: chi non sa che su Telegram si tocca «Avvia»
 *    e poi «REGISTRATI» si ferma lì, e la segreteria non lo vede.
 * 👉 Il passo 3 scrive il bottone com'è nel bot, «👉 REGISTRATI 👈»: il bot
 *    riprende la stessa frase nel suo primo messaggio, e se cambia là cambia qui.
 * 🔄 Voce 28: il link è lo stesso per tutti, quindi non è più «solo per te» e non
 *    scade. Si dice invece da cosa lo riconosce, così chi ha cambiato numero lo
 *    sa prima di provarci.
 */
export function messaggioInvito(nome: string, link: string): string {
  return (
    `Ciao ${nome},\n` +
    `Padel Village ASD ti mette a disposizione gratuitamente un *assistente virtuale* su ` +
    `Telegram, *DEDICATO ESCLUSIVAMENTE A TE*, per:\n` +
    `• prenotare il campo\n` +
    `• ricevere l’avviso quando giochi\n` +
    `• invitare i tuoi amici\n` +
    `• entrare nelle partite aperte che cercano giocatori\n` +
    `• trovarti il campo quando sei libero: glielo scrivi e lui cerca\n\n` +
    `Per attivarlo:\n` +
    `1. apri questo link: ${link}\n` +
    `2. su Telegram tocca «Avvia»\n` +
    `3. tocca «👉 REGISTRATI 👈» e condividi il tuo numero\n\n` +
    `L’assistente ti riconosce dal numero di telefono che hai dato alla Segreteria. Se non hai ` +
    `Telegram, lo scarichi gratis dallo store del telefono.\n\n` +
    `La Segreteria di Padel Village ASD`
  );
}

/**
 * Il messaggio come lo mostra WhatsApp: `*così*` è grassetto. Nel riquadro si vede
 * il grassetto, e copiato o mandato parte con gli asterischi, che WhatsApp legge.
 */
function comeSuWhatsApp(testo: string): React.ReactNode[] {
  return testo.split(/\*([^*\n]+)\*/).map((pezzo, i) => (i % 2 ? <strong key={i}>{pezzo}</strong> : pezzo));
}

/** `nome` è il nome di battesimo (il saluto del messaggio), `nomeIntero` quello della scheda. */
export function InvitoBot({
  id,
  nome,
  nomeIntero,
}: {
  id: string;
  nome: string;
  nomeIntero: string;
}): React.JSX.Element {
  const invito = usaInvitoBot(id);

  return (
    <section className="riquadro bot-socio">
      <h3>Bot dei soci</h3>
      {invito.isPending ? (
        <Attesa cosa="il bot" />
      ) : invito.error ? (
        <Guasto errore={invito.error} />
      ) : invito.data ? (
        <Contenuto v={invito.data} nome={nome} nomeIntero={nomeIntero} />
      ) : null}
    </section>
  );
}

function Contenuto({
  v,
  nome,
  nomeIntero,
}: {
  v: InvitoAlBot;
  nome: string;
  nomeIntero: string;
}): React.JSX.Element {
  /** Il messaggio intero si apre a richiesta: la scheda resta corta. */
  const [mostra, setMostra] = useState(false);
  const [mandato, setMandato] = useState<Mandato | null>(null);
  const [copiato, setCopiato] = useState<'fermo' | 'copiato' | 'nolo'>('fermo');
  // Stabile: il conto alla rovescia dopo «Copiato» non riparte a ogni rilettura.
  const onMandato = useCallback((come: Mandato['come']) => {
    setMandato({ come, alle: new Date() });
    setMostra(false);
    setCopiato('fermo');
  }, []);

  useEffect(() => {
    if (copiato === 'fermo') return;
    // Copiato ⇒ si legge «Copiato» un attimo, poi la riga verde. Non riuscito ⇒
    // il messaggio si apre e resta lì, da selezionare a mano.
    const t =
      copiato === 'copiato'
        ? setTimeout(() => onMandato('copiato'), 1500)
        : setTimeout(() => setCopiato('fermo'), 2500);
    return () => clearTimeout(t);
  }, [copiato, onMandato]);

  if (v.inBot) {
    return (
      <>
        <span className="stato dentro">è nel bot</span>
        <p className="piccolo">Si è registrato {v.joinedAt ? quando(v.joinedAt) : ''}.</p>
      </>
    );
  }

  const perche = !v.phoneE164
    ? 'Serve il telefono nella scheda: il bot lo chiede per riconoscerlo. Aggiungilo con «Modifica l’anagrafica».'
    : !v.link
      ? 'Il bot non si è ancora presentato al gestionale, quindi il link non si può comporre. Riprova quando il bot è acceso.'
      : null;
  const testo = v.link ? messaggioInvito(nome, v.link) : '';

  return (
    <>
      <span className="stato fuori">non è nel bot</span>
      {mandato && (
        <p className="piccolo fatto">
          {mandato.come === 'whatsapp' ? 'Messaggio aperto su WhatsApp' : 'Messaggio copiato'} alle{' '}
          {soloOra(mandato.alle)}.
        </p>
      )}
      {perche ? (
        <p className="piccolo avviso">{perche}</p>
      ) : (
        <>
          <p className="piccolo tenue">
            Il link è lo stesso per tutti i soci: il bot riconosce {nomeIntero} dal suo numero, {v.phoneE164}.
          </p>
          <p className="link">
            <code>{v.link}</code>
          </p>
        </>
      )}
      {!perche && mostra && <p className="messaggio">{comeSuWhatsApp(testo)}</p>}
      <div className="gesti">
        {perche !== null || !v.phoneE164 ? (
          <button type="button" disabled>
            Mandalo su WhatsApp
          </button>
        ) : (
          <a
            className="bottone-pieno"
            href={`https://wa.me/${v.phoneE164.slice(1)}?text=${encodeURIComponent(testo)}`}
            target="_blank"
            rel="noopener noreferrer"
            // WhatsApp si apre nella scheda nuova; qui resta la riga verde.
            onClick={() => onMandato('whatsapp')}
          >
            Mandalo su WhatsApp
          </a>
        )}
        <button
          type="button"
          className="bordato"
          disabled={perche !== null}
          onClick={() => {
            // ⚠️ Se la copia non riesce non si finge: il testo si apre, da selezionare.
            const nolo = () => {
              setCopiato('nolo');
              setMostra(true);
            };
            if (!navigator.clipboard) return nolo();
            void navigator.clipboard
              .writeText(testo)
              .then(() => setCopiato('copiato'))
              .catch(nolo);
          }}
        >
          {copiato === 'copiato' ? 'Copiato' : copiato === 'nolo' ? 'Copialo a mano' : 'Copia il messaggio'}
        </button>
        {!perche && (
          <button type="button" className="bordato" onClick={() => setMostra((m) => !m)}>
            {mostra ? 'Nascondi il messaggio' : 'Mostra il messaggio'}
          </button>
        )}
      </div>
    </>
  );
}
